import { useEffect, useRef } from 'react'

/**
 * Scroll the list while a leg is being dragged near its edge — ROADMAP 9.9.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BROWSER AUTO-SCROLLS THE DOCUMENT, NOT AN INNER SCROLL PORT
 *
 * HTML5 drag-and-drop gives you edge auto-scroll for free on the *viewport*:
 * drag to the bottom of the window and the page scrolls. It gives you nothing
 * for an element with its own `overflow: auto`, and that is what the positions
 * list is (`.tr-dscroll`). The page itself does not scroll at all here — the
 * drawer is a flex column pinned to the viewport — so the free behaviour never
 * had anything to act on.
 *
 * The consequence, reported 2026-10-30 with 23 positions open: a leg can only
 * be dropped on a basket that happens to be on screen at the moment the drag
 * starts. Dragging from the MANUAL block, which renders below every basket, to
 * a strategy near the top is not merely awkward — it is impossible, because
 * the mouse button is already down and nothing will scroll under it.
 *
 * SCROLLING IS NOT A REFLOW, which is the thing this screen has to be careful
 * about. `BookDrawer` carries a scar from a banner that appeared and
 * auto-dismissed above the cards: that RELAYOUT moved the row under the cursor
 * and Chrome aborted the drag mid-gesture. Changing `scrollTop` moves the
 * viewport over an unchanged layout, so the dragged row keeps its box and the
 * drag survives.
 *
 * WHY A rAF LOOP RATHER THAN SCROLLING INSIDE `dragover`
 *
 * `dragover` fires only when the pointer MOVES (plus an irregular repeat that
 * differs by browser). Holding still at the bottom edge — exactly what a
 * trader does while waiting for the list to come to them — would deliver a
 * handful of events and stop. A `requestAnimationFrame` loop reads the last
 * known pointer position and scrolls continuously at a velocity, so holding
 * still keeps scrolling and the speed is in px/second rather than
 * px/whenever-an-event-happened.
 *
 * WHY THE LISTENER IS ON `document`
 *
 * `dragleave` fires on the element being LEFT and bubbles, so a listener on
 * the port cannot tell "the pointer left the list" from "the pointer moved
 * from one card to the next". Tracking the pointer globally and judging it
 * against the port's own rectangle has no such ambiguity, and it also lets the
 * drag scroll while the pointer is just OUTSIDE the top edge, which is where a
 * hand reaching for a basket above naturally ends up.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** How close to an edge the pointer comes before it scrolls, in px. */
const EDGE = 56

/** Speed at the very edge, in px per second. Ramped down to 0 at `EDGE`. */
const MAX_SPEED = 900

/**
 * The longest step a single frame may advance, in seconds.
 *
 * A backgrounded tab, or one frame lost to a long task, otherwise hands the
 * next frame a `dt` of several hundred milliseconds and the list jumps by a
 * screenful. Clamped to ~3 frames at 60 Hz.
 */
const MAX_FRAME = 0.05

/**
 * Attach the returned ref to the scrolling element.
 *
 * `active` is the caller's own "a leg is in the hand" state — the loop only
 * exists while it is true, so nothing runs and no listener is bound on a
 * screen where nobody is dragging.
 */
export function useDragAutoScroll(active: boolean) {
  const port = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = port.current
    if (!active || el === null) return

    /** Last known pointer position in viewport coordinates, or null. */
    let at: { x: number; y: number } | null = null
    let frame = 0
    /*
     * -1, not 0, for "no previous frame". A frame timestamp of 0 is legal —
     * it is the document's own time origin — and using it as the sentinel
     * means the clock never starts and nothing ever scrolls.
     */
    let last = -1

    const track = (e: DragEvent): void => {
      at = { x: e.clientX, y: e.clientY }
    }

    const step = (now: number): void => {
      frame = requestAnimationFrame(step)
      const dt = last < 0 ? 0 : Math.min((now - last) / 1000, MAX_FRAME)
      last = now
      if (at === null || dt === 0) return

      const box = el.getBoundingClientRect()
      /*
       * Horizontally outside the list is someone dragging over the chain, and
       * vertically far outside is a hand that has left the drawer entirely.
       * Neither should move this list under them.
       */
      if (at.x < box.left || at.x > box.right) return
      if (at.y < box.top - EDGE || at.y > box.bottom + EDGE) return

      const fromTop = at.y - box.top
      const fromBottom = box.bottom - at.y
      const speed = (depth: number): number => (Math.min(depth, EDGE) / EDGE) * MAX_SPEED

      const velocity =
        fromTop < EDGE ? -speed(EDGE - fromTop) : fromBottom < EDGE ? speed(EDGE - fromBottom) : 0
      if (velocity === 0) return

      el.scrollTop += velocity * dt
    }

    document.addEventListener('dragover', track)
    frame = requestAnimationFrame(step)

    return () => {
      document.removeEventListener('dragover', track)
      cancelAnimationFrame(frame)
    }
  }, [active])

  return port
}
